import React, { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { AlertCircle } from 'lucide-react';

interface EmailActionModalProps {
    open: boolean;
    onClose: () => void;
    email: any;
    actionType: string;
    onConfirm: (notes: string) => void;
}

export default function EmailActionModal({ open, onClose, email, actionType, onConfirm }: EmailActionModalProps) {
    const [notes, setNotes] = useState('');

    const handleConfirm = () => {
        onConfirm(notes);
        setNotes('');
    };

    const getTitle = () => {
        if (actionType === 'blacklist') return 'Confirm Block & Unsubscribe';
        if (actionType === 'archive') return 'Archive Email';
        return 'Confirm Action';
    };

    const getDescription = () => {
        if (actionType === 'blacklist') {
            return `Are you sure you want to block ${email?.sender}? This will unsubscribe them from ALL lists and add them to the global blacklist.`;
        }
        return `Are you sure you want to perform this action on email from ${email?.sender}?`;
    };

    return (
        <Transition appear show={open} as={Fragment}>
            <Dialog as="div" className="relative z-10" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-25" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                <Dialog.Title
                                    as="h3"
                                    className="text-lg font-medium leading-6 text-gray-900 flex items-center gap-2"
                                >
                                    {actionType === 'blacklist' && <AlertCircle className="text-red-500" size={20} />}
                                    {getTitle()}
                                </Dialog.Title>
                                <div className="mt-2">
                                    <p className="text-sm text-gray-500">
                                        {getDescription()}
                                    </p>
                                </div>

                                <div className="mt-4">
                                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                                        Notes (Optional)
                                    </label>
                                    <input
                                        type="text"
                                        name="notes"
                                        id="notes"
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2"
                                        placeholder="Reason for action..."
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                    />
                                </div>

                                <div className="mt-6 flex justify-end gap-3">
                                    <button
                                        type="button"
                                        className="inline-flex justify-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
                                        onClick={onClose}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className={`inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${actionType === 'blacklist'
                                                ? 'bg-red-600 hover:bg-red-700 focus-visible:ring-red-500'
                                                : 'bg-indigo-600 hover:bg-indigo-700 focus-visible:ring-indigo-500'
                                            }`}
                                        onClick={handleConfirm}
                                    >
                                        Confirm
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}
